import Swal from 'sweetalert2';
import { escapeHtml } from '@/utils/escapeHtml';

/**
 * Helpers do popup "wa-connect": um SweetAlert de escolha única onde cada
 * opção é um card clicável, em vez do `input: 'radio'` nativo.
 *
 * O estilo vive em `src/assets/css/wa-connect-popup.css` (global, porque o
 * SweetAlert é montado fora da árvore de componentes) e é carregado pelo
 * `main.ts`. Usado pelo fluxo de conexão de WhatsApp na área do app e pelas
 * conversões para híbrido na área admin.
 */

/** Reexportado: quem já importava daqui continua funcionando. */
export { escapeHtml };

export interface WaCardOpcao<T extends string = string> {
    /** Valor devolvido quando o card está selecionado. */
    value: T;
    /** Classe do ícone (Remix Icon), ex.: `ri-cloud-line`. */
    icon: string;
    titulo: string;
    descricao?: string;
}

/**
 * Tema de cor do popup. Só troca as variáveis CSS de destaque
 * (`--wac-*` em `wa-connect-popup.css`); layout e comportamento são os mesmos.
 */
export type WaConnectTema = 'whatsapp' | 'messenger' | 'instagram';

export interface EscolherEmCardsParams<T extends string = string> {
    /** Ícone do círculo colorido no header. */
    icone?: string;
    titulo: string;
    /** HTML — use {@link escapeHtml} em qualquer dado interpolado. */
    descricao?: string;
    opcoes: WaCardOpcao<T>[];
    /** Card já marcado ao abrir. Sem isso, o popup abre sem seleção. */
    valorInicial?: T;
    /** Rodapé de aviso abaixo dos cards. HTML, mesma regra da `descricao`. */
    nota?: string;
    confirmarTexto?: string;
    cancelarTexto?: string;
    /** Mensagem quando o usuário confirma sem escolher. */
    mensagemValidacao?: string;
    /**
     * Rola a lista de cards em vez de esticar o popup. Ligue quando o número
     * de opções é dinâmico (ex.: um card por servidor Evolution).
     */
    scroll?: boolean;
    /** Cor de destaque. Default `whatsapp` (verde), como antes. */
    tema?: WaConnectTema;
}

/** Monta o HTML de um card de opção. O radio fica escondido; o estado visual vem do `:has()`. */
function cardHtml(nome: string, indice: number, opcao: WaCardOpcao, marcado: boolean): string {
    const id = `${nome}-opt-${indice}`;
    const descricao = opcao.descricao
        ? `<span class="wa-option-desc">${escapeHtml(opcao.descricao)}</span>`
        : '';

    return `
        <label class="wa-option-card" for="${id}">
            <input type="radio" name="${nome}" id="${id}" value="${escapeHtml(opcao.value)}" class="wa-option-input" ${marcado ? 'checked' : ''}>
            <span class="wa-option-icon"><i class="${escapeHtml(opcao.icon)}"></i></span>
            <span class="wa-option-text">
                <span class="wa-option-title">${escapeHtml(opcao.titulo)}</span>
                ${descricao}
            </span>
            <span class="wa-option-check"><i class="ri-check-line"></i></span>
        </label>
    `;
}

/**
 * Abre o popup de escolha em cards e resolve com o valor escolhido, ou `null`
 * se o usuário cancelou/fechou.
 */
export async function escolherEmCards<T extends string = string>({
    icone = 'ri-whatsapp-fill',
    titulo,
    descricao,
    opcoes,
    valorInicial,
    nota,
    confirmarTexto = 'Continuar',
    cancelarTexto = 'Cancelar',
    mensagemValidacao = 'Escolha uma opção',
    scroll = false,
    tema = 'whatsapp',
}: EscolherEmCardsParams<T>): Promise<T | null> {
    const nomeGrupo = 'wa-card-opcao';
    const valoresValidos = new Set<string>(opcoes.map(o => o.value));

    const { value: escolhido } = await Swal.fire({
        title: '',
        html: `
            <div class="wa-connect-content">
                <div class="wa-connect-header">
                    <div class="wa-connect-icon"><i class="${escapeHtml(icone)}"></i></div>
                    <h3 class="wa-connect-title">${escapeHtml(titulo)}</h3>
                    ${descricao ? `<p class="wa-connect-desc">${descricao}</p>` : ''}
                </div>
                <div class="wa-connect-body${scroll ? ' wa-connect-body--scroll' : ''}">
                    ${opcoes.map((o, i) => cardHtml(nomeGrupo, i, o, o.value === valorInicial)).join('')}
                </div>
                ${nota ? `<div class="wa-connect-note"><i class="ri-information-line"></i><span>${nota}</span></div>` : ''}
            </div>
        `,
        showCancelButton: true,
        confirmButtonText: confirmarTexto,
        cancelButtonText: cancelarTexto,
        buttonsStyling: false,
        focusConfirm: false,
        customClass: {
            popup:
                tema === 'whatsapp'
                    ? 'wa-connect-popup'
                    : `wa-connect-popup wa-connect-popup--${tema}`,
            confirmButton: 'btn-wa-confirm',
            cancelButton: 'btn-wa-cancel',
            actions: 'wa-connect-actions',
        },
        preConfirm: () => {
            const marcado = document.querySelector<HTMLInputElement>(
                `input[name="${nomeGrupo}"]:checked`,
            )?.value;

            if (!marcado) {
                Swal.showValidationMessage(mensagemValidacao);
                return false;
            }

            return marcado;
        },
    });

    return typeof escolhido === 'string' && valoresValidos.has(escolhido) ? (escolhido as T) : null;
}
